import { Link } from '@tanstack/react-router';
import {
    Activity,
    ArrowUpRight,
    ChevronRight,
    ExternalLink,
    MessageSquare,
    Pencil,
    PhoneCall,
    RotateCcw,
    ScrollText,
    UserRound,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    type NoteSourceLink,
    type NoteTraceLinks,
} from '@apps/web/features/admin_agent_notes/model/note_trace_links';
import {
    NoteAddToChatButton,
    NoteConversationAddToChatButton,
} from '@apps/web/features/admin_agent_notes/ui/note_add_to_chat';
import { useNoteEntityModals } from '@apps/web/features/admin_agent_notes/ui/note_entity_modals';
import { NoteProcessingStepper } from '@apps/web/features/admin_agent_notes/ui/note_processing_stepper';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentNoteRead } from '@libraries/typescript/api_client';

function DisabledTraceButton({
    label,
    reason,
    icon,
}: {
    readonly label: string;
    readonly reason: string;
    readonly icon: React.ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={true}
                        aria-label={`${label} unavailable: ${reason}`}
                    >
                        {icon}
                        {label}
                    </Button>
                </span>
            </TooltipTrigger>
            <TooltipContent>{reason}</TooltipContent>
        </Tooltip>
    );
}

/** The note's origin link, rendered with a channel-specific icon. */
function SourceButton({ sourceLink }: { readonly sourceLink: NoteSourceLink }) {
    switch (sourceLink.kind) {
        case 'agent_trace':
            return (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <Link to={sourceLink.to} params={sourceLink.params}>
                        <Activity className="h-3.5 w-3.5" />
                        Source
                    </Link>
                </Button>
            );
        case 'agent_run':
            return (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <Link to={sourceLink.to} params={sourceLink.params}>
                        <Activity className="h-3.5 w-3.5" />
                        Source
                    </Link>
                </Button>
            );
        case 'voice_call':
            return (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <Link to={sourceLink.to} params={sourceLink.params}>
                        <PhoneCall className="h-3.5 w-3.5" />
                        Source
                    </Link>
                </Button>
            );
        case 'sms_conversation':
            return (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <Link to={sourceLink.to} search={sourceLink.search}>
                        <MessageSquare className="h-3.5 w-3.5" />
                        Source
                    </Link>
                </Button>
            );
        default: {
            const exhaustive: never = sourceLink;
            return exhaustive;
        }
    }
}

/**
 * Action row for a note: retry (any settled note that wrote nothing
 * durable), Source (its origin), the Classification Braintrust run, and the
 * Logfire logs.
 */
export function NoteTraceActions({
    note,
    links,
    isRetrying,
    onRetry,
}: {
    readonly note: AdminAgentNoteRead;
    readonly links: NoteTraceLinks;
    readonly isRetrying: boolean;
    readonly onRetry: (noteId: string) => void;
}) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {note.is_retryable && (
                <Button
                    variant={
                        note.status === 'failed' ? 'destructive' : 'outline'
                    }
                    size="sm"
                    className="gap-1.5"
                    disabled={isRetrying}
                    onClick={() => {
                        onRetry(note.id);
                    }}
                >
                    <RotateCcw
                        className={cn(
                            'h-3.5 w-3.5',
                            isRetrying ? 'animate-spin' : '',
                        )}
                    />
                    {isRetrying ? 'Retrying…' : 'Retry classification'}
                </Button>
            )}
            {links.sourceLink ? (
                <SourceButton sourceLink={links.sourceLink} />
            ) : (
                <DisabledTraceButton
                    label="Source"
                    reason="No source recorded for this note"
                    icon={<ArrowUpRight className="h-3.5 w-3.5" />}
                />
            )}
            {links.classificationBraintrustUrl ? (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <a
                        href={links.classificationBraintrustUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Classification
                    </a>
                </Button>
            ) : (
                <DisabledTraceButton
                    label="Classification"
                    reason="No classification run recorded for this note"
                    icon={<ExternalLink className="h-3.5 w-3.5" />}
                />
            )}
            {links.classificationTraceUrl ? (
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    asChild={true}
                >
                    <a
                        href={links.classificationTraceUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ScrollText className="h-3.5 w-3.5" />
                        Logs
                    </a>
                </Button>
            ) : (
                <DisabledTraceButton
                    label="Logs"
                    reason="No classification logs yet"
                    icon={<ScrollText className="h-3.5 w-3.5" />}
                />
            )}
            <NoteAddToChatButton
                note={note}
                showLabel={true}
                idleLabel="Add note to chat"
            />
            <NoteConversationAddToChatButton
                note={note}
                showLabel={true}
                idleLabel="Add conversation to chat"
            />
        </div>
    );
}

/** Note content, entity fields, processing stepper, and raw JSON. */
export function NoteDetailContent({
    note,
    attemptsError = false,
    isRetrying = false,
    onRetryWithContent,
}: {
    readonly note: AdminAgentNoteRead;
    readonly attemptsError?: boolean;
    readonly isRetrying?: boolean;
    // Retry keeps the editor's text when the server rejects the new content.
    readonly onRetryWithContent?: (
        noteId: string,
        content: string,
    ) => Promise<boolean>;
}) {
    const { caregiver, client } = note;
    const { openCaregiver, openClient, openPlaybook, entityModals } =
        useNoteEntityModals(note.organization_id);
    const [editedContent, setEditedContent] = useState<string | null>(null);
    const canEdit = onRetryWithContent !== undefined && note.is_retryable;
    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
                <span className="flex items-center justify-between">
                    <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                        Note content
                    </span>
                    {canEdit && editedContent === null && (
                        <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-[11px]"
                            onClick={() => {
                                setEditedContent(note.content);
                            }}
                        >
                            <Pencil aria-hidden={true} className="h-3 w-3" />
                            Edit and retry
                        </button>
                    )}
                </span>
                {editedContent === null ? (
                    <p className="bg-muted/40 rounded-md border px-3 py-2 text-sm whitespace-pre-wrap">
                        {note.content}
                    </p>
                ) : (
                    <div className="flex flex-col gap-1.5">
                        <Textarea
                            value={editedContent}
                            onChange={(event) => {
                                setEditedContent(event.target.value);
                            }}
                            rows={4}
                            className="text-sm"
                            aria-label="Edited note content"
                        />
                        <div className="flex items-center gap-1.5">
                            <Button
                                size="sm"
                                disabled={
                                    isRetrying ||
                                    editedContent.trim().length === 0
                                }
                                onClick={() => {
                                    void onRetryWithContent?.(
                                        note.id,
                                        editedContent.trim(),
                                    ).then((accepted) => {
                                        if (accepted) {
                                            setEditedContent(null);
                                        }
                                    });
                                }}
                            >
                                Save and retry classification
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={isRetrying}
                                onClick={() => {
                                    setEditedContent(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            {(caregiver || client) && (
                <div className="flex flex-wrap items-center gap-2">
                    {caregiver && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 gap-1 px-2 text-xs"
                            onClick={() => {
                                openCaregiver(caregiver.id);
                            }}
                        >
                            <UserRound aria-hidden="true" className="h-3 w-3" />
                            {caregiver.name}
                            <span className="text-muted-foreground font-normal">
                                caregiver
                            </span>
                        </Button>
                    )}
                    {client && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 gap-1 px-2 text-xs"
                            onClick={() => {
                                openClient(client.id);
                            }}
                        >
                            <UserRound aria-hidden="true" className="h-3 w-3" />
                            {client.name}
                            <span className="text-muted-foreground font-normal">
                                client
                            </span>
                        </Button>
                    )}
                </div>
            )}
            <div className="flex flex-col gap-1.5">
                <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                    Processing
                </span>
                <NoteProcessingStepper
                    note={note}
                    attemptsError={attemptsError}
                    onOpenCaregiver={openCaregiver}
                    onOpenClient={openClient}
                    onOpenPlaybook={openPlaybook}
                />
            </div>
            {entityModals}
            <Collapsible>
                <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs font-medium">
                    <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
                    Raw JSON
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <pre className="bg-muted/40 mt-2 max-h-[320px] overflow-auto rounded-md border p-3 font-mono text-[11px]">
                        {JSON.stringify(note, null, 2)}
                    </pre>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
}
